// src/BookDetails.tsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import getStoryById from "common/realBackend/blog/getBlogById";
import getAllBookEpisodes from "common/realBackend/blog/getAllBookEpisodes";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Input,
  FormGroup,
  Label,
  Button,
  Spinner,
  Alert
} from "reactstrap";
import { EditorState, convertToRaw, ContentState, convertFromHTML } from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import draftToHtml from "draftjs-to-html";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import getEpisodeById from "common/realBackend/blog/getEpisodeById";
import { extractDifference } from "helpers/extract-differemce";
import updateEpisode from "common/realBackend/blog/updateEpisode";
import createOneShotEpisode from "common/realBackend/blog/createOneSHotEpisode";


const BookDetails = () => {
  document.title = "Book Details | AAOOA Admin";
  const [content, setContent] = useState(null);
  const [existingEpisode, setEpisodes] = useState(undefined)
  const { type, id, episodeId } = useParams();
  const [loading, setLoading] = useState(false);
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  const [storyTextEditorState, setStoryTextEditorState] = useState(EditorState.createEmpty());

  const validationSchema = Yup.object().shape({
    duration: Yup.number().required("Duration is required"),
    audio: Yup.mixed().nullable(),
    video: Yup.string().nullable()
  });



  const handleSubmit = async (values) => {

    setLoading(true);
    try {
      if (existingEpisode) {
        const diff = extractDifference(existingEpisode, values);
        console.log(diff)
        if (!Object.keys(diff).length) {
          setAlertMessage("No change detected");
          setAlertVisible(true);
          return;
        }
        await updateEpisode({ id: existingEpisode.id, ...diff });
        setAlertMessage("Episode updated successfully!");

        setAlertVisible(true);

      } else {
        const episode = await createOneShotEpisode({ story_id: id, ...values });
        setEpisodes(episode);
        setAlertMessage("Episode created successfully!");

        setAlertVisible(true);
      }
    } catch (error) {
      console.error("Error creating:", error);
      setAlertMessage("Error updating. Please try again.");
      setAlertVisible(true);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    const fetchContent = async () => {
      try {
        const data = await getStoryById(id);
        const episodeData = await getAllBookEpisodes(id);
        setContent(data.data);
        const episode = episodeData.data[0];
        setEpisodes(episodeData.data[0] || null);
        if (episode) {
          const contentBlock = convertFromHTML(episode.content);
          if (contentBlock) {
            const contentState = ContentState.createFromBlockArray(
              contentBlock.contentBlocks
            );
            const newEditorState = EditorState.createWithContent(contentState);
            setStoryTextEditorState(newEditorState);
          }
        }

      } catch (error) {
        console.error(`Error fetching ${type} with ID ${id}${episodeId ? ` and episode ID ${episodeId}` : ""}:`, error);
      }
    };
    fetchContent();
  }, [type, id, episodeId]);

  if (!content) {
    return <p>Loading...</p>;
  }

  const extractYouTubeID = (url) => {
    if (!url) return null; // Return null if the URL is falsy (null, undefined, empty string)

    const match = url.match(/(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([^&]+)/) ||
      url.match(/(?:https?:\/\/)?(?:www\.)?youtu\.be\/([^&]+)/);
    return match ? match[1] : null;
  };

  const handleEditorChange = (state, setFieldValue, fieldName) => {
    const htmlContent = draftToHtml(convertToRaw(state.getCurrentContent()));

    setFieldValue(fieldName, htmlContent);
    setStoryTextEditorState(state);

  };

  const youtubeID = extractYouTubeID(content.videoLinkString);

  return (
    <React.Fragment>

      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={type === "one-shot" ? "One-Shot" : "Series"} breadcrumbItem="Details" />\
          {alertVisible && (
            <Alert color={alertMessage.includes("Error") ? "danger" : "success"}
              toggle={() => setAlertVisible(false)}
              style={{ position: 'fixed', top: '1rem', right: '1rem', zIndex: 1050 }}>
              {alertMessage}
            </Alert>
          )}

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="pt-3">
                    <div className="row justify-content-center">
                      <div className="col-xl-8">
                        <div className="text-center">
                          <h4>{content.name}</h4>
                          <p className="text-muted mb-4">
                            <i className="mdi mdi-calendar me-1"></i> {new Date(content.created_at).toLocaleDateString()}
                          </p>
                        </div>

                        <hr />

                        <div className="my-5">
                          <img
                            src={content.image}
                            alt="Cover"
                            className="img-thumbnail mx-auto d-block"
                          />
                        </div>

                        <hr />
                        {
                          existingEpisode !== undefined ?
                            <Formik
                              enableReinitialize
                              initialValues={{
                                duration: existingEpisode?.duration,
                                video: existingEpisode?.video,
                              }}
                              validationSchema={validationSchema}
                              onSubmit={handleSubmit}
                            >
                              {({ setFieldValue, values }) => (
                                <Form>
                                  <FormGroup className="mb-4">
                                    <Label htmlFor="duration" className="col-form-label">Episode Duration</Label>
                                    <Field
                                      id="duration"
                                      name="duration"
                                      as={Input}
                                      placeholder="Enter Episode Duration..."
                                    />
                                    <ErrorMessage name="duration" component="div" className="text-danger" />
                                  </FormGroup>


                                  <FormGroup className="mb-4">
                                    <Label className="col-form-label">Content</Label>
                                    <Editor
                                      editorState={storyTextEditorState}
                                      toolbarClassName="toolbarClassName"
                                      wrapperClassName="wrapperClassName"
                                      editorClassName="editorClassName"
                                      placeholder="Write the main story content here..."
                                      onEditorStateChange={(state) => handleEditorChange(state, setFieldValue, "content")}
                                    />
                                    <ErrorMessage name="content" component="div" className="text-danger" />
                                  </FormGroup>

                                  <FormGroup className="mb-4">
                                    <Label className="col-form-label">Upload Audio</Label>
                                    <Input
                                      type="file"
                                      className="form-control"
                                      onChange={(e) => setFieldValue("audio", e.currentTarget.files[0])}
                                    />
                                    {!!existingEpisode?.audio && (
                                      <div className="mt-2">
                                        <audio controls>
                                          <source src={existingEpisode.audio} type="audio/mpeg" />
                                        </audio>
                                      </div>
                                    )}
                                  </FormGroup>

                                  <FormGroup className="mb-4">
                                    <Label htmlFor="video" className="col-form-label">Video Link</Label>
                                    <Field
                                      id="video"
                                      name="video"
                                      as={Input}
                                      placeholder="Enter Video Link..."
                                    />
                                  </FormGroup>

                                  <Row className="justify-content-end">
                                    <Col lg="10">
                                      <Button type="submit" color="primary" disabled={loading} onClick={() => console.log(values)}>
                                        {loading ? <Spinner size="sm" /> : existingEpisode ? "Update Episode" : "Create Episode"}
                                      </Button>
                                    </Col>
                                  </Row>
                                </Form>
                              )}
                            </Formik> : <Spinner color="primary" />
                        }

                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default BookDetails;
